/**
 * Expanded Question Bank: Domain 6 — Processing Speed & Attention (سرعة المعالجة والانتباه)
 * 15 Stochastic Rapid Discrimination & Perceptual Matching Templates
 */

import { renderOptionSvg, DrawShapes } from './svg-builder.js';

export const EXPANDED_SPEED_TEMPLATES = [
  // 1. Symbol-Digit Decoding Key
  {
    id: 'speed_symbol_digit_decoding',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2],
    expectedTimeSeconds: 20,
    generate(prng, difficulty = 1) {
      // Key: ★=4, ▲=7, ■=2, ●=9
      const key = [
        { sym: '★', val: '4' },
        { sym: '▲', val: '7' },
        { sym: '■', val: '2' },
        { sym: '●', val: '9' }
      ];

      // Target sequence of 4 symbols
      const seq = ['▲', '★', '●', '■'];
      const correctCode = '7492';

      const distractors = ['7429', '4792', '7942'];
      const allChoices = prng.shuffle([correctCode, ...distractors]);
      const correctIndex = allChoices.indexOf(correctCode);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-muted">مفتاح التشفير السريع (Symbol-Digit Coding):</div>
          <div class="iq-stimulus-card-subtitle">[ ★ = 4 ]   •   [ ▲ = 7 ]   •   [ ■ = 2 ]   •   [ ● = 9 ]</div>
          <div class="iq-stimulus-card-highlight">فك تشفير السلسلة: [ ▲  ★  ●  ■ ]</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-muted">Symbol-Digit Coding Key:</div>
          <div class="iq-stimulus-card-subtitle">[ ★ = 4 ]   •   [ ▲ = 7 ]   •   [ ■ = 2 ]   •   [ ● = 9 ]</div>
          <div class="iq-stimulus-card-highlight">Decode the stream: [ ▲  ★  ●  ■ ]</div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `بناءً على مفتاح التشفير المعروض، ما هو الرمز الرقمي المطابق لتسلسل الأشكال (▲ ★ ● ■)؟`,
        questionText_en: `Using the decoding key, what numerical string decodes the symbol stream (▲ ★ ● ■)?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c,
          text_en: c,
          value: c
        })),
        correctIndex,
        explanation_ar: `▲ = 7 ، ★ = 4 ، ● = 9 ، ■ = 2. الشفرة المطابقة تماماً هي: 7492.`,
        explanation_en: `▲=7, ★=4, ●=9, ■=2, yielding the exact sequence 7492.`
      };
    }
  },

  // 2. Rapid Hash Key Match Verification
  {
    id: 'speed_hash_exact_match',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2],
    expectedTimeSeconds: 20,
    generate(prng, difficulty = 1) {
      const baseKey = 'K9X-4M7P';
      const correctAns = 'K9X-4M7P';

      const distractors = [
        'K9X-4N7P', // N instead of M
        'K9X-4M7R', // R instead of P
        'K8X-4M7P'  // 8 instead of 9
      ];

      const allChoices = prng.shuffle([correctAns, ...distractors]);
      const correctIndex = allChoices.indexOf(correctAns);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-muted">الرمز المرجعي المستهدف:</div>
          <div class="iq-stimulus-card-highlight" style="font-family: monospace; font-size: 1.25rem; letter-spacing: 0.1em;">${baseKey}</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-muted">Target Reference Key:</div>
          <div class="iq-stimulus-card-highlight" style="font-family: monospace; font-size: 1.25rem; letter-spacing: 0.1em;">${baseKey}</div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `حدد الخيار الذي يطابق الرمز المرجعي المستهدف بالكامل دون أي اختلاف في حرف أو رقم:`,
        questionText_en: `Select the option matching the target key exactly without any character substitution:`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c,
          text_en: c,
          value: c
        })),
        correctIndex,
        explanation_ar: `الخيار (${correctAns}) مطابق تماماً لكافة خانات الرمز المرجعي دون أي تغيير.`,
        explanation_en: `Option (${correctAns}) matches the reference string identically character for character.`
      };
    }
  },

  // 3. Heptagon Identification (Count Sides Rapidly)
  {
    id: 'speed_polygon_heptagon_scan',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2],
    expectedTimeSeconds: 25,
    generate(prng, difficulty = 1) {
      const correctAns_ar = 'المضلع السباعي (Heptagon) ذو 7 أضلاع';
      const correctAns_en = 'Heptagon (7 sides)';

      const distractors = [
        { ar: 'المضلع السداسي (Hexagon) ذو 6 أضلاع', en: 'Hexagon (6 sides)' },
        { ar: 'المضلع الثماني (Octagon) ذو 8 أضلاع', en: 'Octagon (8 sides)' },
        { ar: 'المضلع الخماسي (Pentagon) ذو 5 أضلاع', en: 'Pentagon (5 sides)' }
      ];

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      // Draw heptagon SVG
      let pts = [];
      for (let i = 0; i < 7; i++) {
        const ang = (i * 2 * Math.PI) / 7 - Math.PI / 2;
        pts.push(`${190 + 35 * Math.cos(ang)},${55 + 35 * Math.sin(ang)}`);
      }

      const stimulusSvg = `<svg viewBox="0 0 380 110" xmlns="http://www.w3.org/2000/svg">
        <rect width="380" height="110" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255,255,255,0.1)"/>
        <polygon points="${pts.join(' ')}" fill="rgba(56, 189, 248, 0.2)" stroke="#38bdf8" stroke-width="2.5"/>
      </svg>`;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `بالملاحظة السريعة لعدد أضلاع وزوايا هذا المضلع المنتظم، ما هو اسمه الهندسي الدقيق؟`,
        questionText_en: `Quickly inspecting the number of sides and vertices, what is the precise geometric name of this polygon?`,
        stimulusHtml: stimulusSvg,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `المضلع يحتوي على 7 أضلاع و 7 زوايا متطابقة تماماً، وهو مضلع سباعي (Heptagon).`,
        explanation_en: `The polygon has exactly 7 sides and vertices, making it a regular heptagon.`
      };
    }
  },

  // 4. Stroop Color Congruence Detection
  {
    id: 'speed_stroop_congruence',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2],
    expectedTimeSeconds: 20,
    generate(prng, difficulty = 1) {
      // 4 words displayed. Only ONE is color-congruent (text matches its physical font color)
      // Word 1: "أحمر" in Blue
      // Word 2: "أخضر" in Green (CONGRUENT!)
      // Word 3: "أصفر" in Red
      // Word 4: "أزرق" in Yellow
      const correctAns_ar = 'الكلمة (أخضر) المكتوبة باللون الأخضر الفعلي';
      const correctAns_en = 'The word "Green" rendered in green font';

      const distractors = [
        { ar: 'الكلمة (أحمر) المكتوبة باللون الأزرق', en: 'The word "Red" rendered in blue' },
        { ar: 'الكلمة (أصفر) المكتوبة باللون الأحمر', en: 'The word "Yellow" rendered in red' },
        { ar: 'الكلمة (أزرق) المكتوبة باللون الأصفر', en: 'The word "Blue" rendered in yellow' }
      ];

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-row" style="display: flex; justify-content: space-around; width: 100%; font-size: 1.25rem; font-weight: 700;">
            <span style="color: #38bdf8;">أحمر</span>
            <span style="color: #10b981;">أخضر</span>
            <span style="color: #ef4444;">أصفر</span>
            <span style="color: #f59e0b;">أزرق</span>
          </div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-row" style="display: flex; justify-content: space-around; width: 100%; font-size: 1.25rem; font-weight: 700;">
            <span style="color: #38bdf8;">Red</span>
            <span style="color: #10b981;">Green</span>
            <span style="color: #ef4444;">Yellow</span>
            <span style="color: #f59e0b;">Blue</span>
          </div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في اختبار ستروب البصري السريع، أي كلمة من الكلمات المعروضة يتطابق معناها اللغوي مع لون خطها الفعلي (متطابقة دلالياً وبصرياً)؟`,
        questionText_en: `In the rapid Stroop task, which word matches its physical font ink color (congruent)?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `كلمة (أخضر) هي الوحيدة المكتوبة باللون الأخضر بالفعل؛ سائر الكلمات ألوان خطوطها متعارضة مع معناها.`,
        explanation_en: `The word "Green" is the only one drawn in green ink, fulfilling Stroop congruence.`
      };
    }
  },

  // 5. Dual Symmetry Reflection Axis
  {
    id: 'speed_dual_symmetry_axis',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2],
    expectedTimeSeconds: 25,
    generate(prng, difficulty = 1) {
      // Which shape has BOTH vertical and horizontal line symmetry?
      // Rectangle / Diamond has both.
      // Arrow has only 1.
      // Scalene triangle has 0.
      // Parallelogram has 0 line symmetries (only 180 rotational).
      const correctAns_ar = 'المستطيل المتناظر (محور رأسي + محور أفقي)';
      const correctAns_en = 'Symmetric Rectangle (Vertical + Horizontal axes)';

      const distractors = [
        { ar: 'السهم الإرشادي (محور واحد فقط)', en: 'Directional Arrow (1 axis only)' },
        { ar: 'متوازي الأضلاع العام (صفر محاور انعكاس)', en: 'General Parallelogram (0 axes)' },
        { ar: 'شبه المنحرف قائم الزاوية (صفر محاور)', en: 'Right Trapezoid (0 axes)' }
      ];

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">المطلوب: شكل هندسي يمتلك محوري تماثل انعكاسي</div>
          <div class="iq-stimulus-card-body">(محور تماثل رأسي ومحور تماثل أفقي معاً)</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">Target: Geometric shape with dual reflection symmetry</div>
          <div class="iq-stimulus-card-body">(Both vertical and horizontal reflection axes)</div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `أي من الأشكال الهندسية التالية يتمتع بخاصية التماثل الانعكاسي المزدوج (محور رأسي ومحور أفقي معاً)؟`,
        questionText_en: `Which geometric shape possesses dual line reflection symmetry (both vertical and horizontal)?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `المستطيل ينطوي على تماثل تام عند طيه رأسياً عبر منتصفه، وعند طيه أفقياً عبر منتصفه (محورا تماثل).`,
        explanation_en: `A rectangle possesses two perpendicular axes of reflectional symmetry.`
      };
    }
  },

  // 6. Rapid Identical Character Pair Count
  {
    id: 'speed_identical_pairs_count',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2],
    expectedTimeSeconds: 25,
    generate(prng, difficulty = 1) {
      // 4 pairs shown:
      // Pair 1: #A79 == #A79 (YES)
      // Pair 2: 8B&K == 8B%K (NO)
      // Pair 3: X4*P == X4*P (YES)
      // Pair 4: 92~Q == 92-Q (NO)
      // Total identical pairs = 2
      const correctVal = 2;
      const distSet = new Set([1, 3, 4]);
      const distractors = Array.from(distSet);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const stimulusHtml = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-table">
            <div class="iq-stimulus-row"><span>1) #A79</span><span>|</span><span>#A79</span></div>
            <div class="iq-stimulus-row"><span>2) 8B&amp;K</span><span>|</span><span>8B%K</span></div>
            <div class="iq-stimulus-row"><span>3) X4*P</span><span>|</span><span>X4*P</span></div>
            <div class="iq-stimulus-row"><span>4) 92~Q</span><span>|</span><span>92-Q</span></div>
          </div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `بالمطابقة البصرية السريعة بين الطرفين في كل سطر، كم زوجاً متطابقاً تماماً بنسبة 100% بين الأزواج الأربعة؟`,
        questionText_en: `Rapidly comparing both sides in each line, how many of the 4 pairs are 100% identical?`,
        stimulusHtml,
        stimulusHtml_ar: stimulusHtml,
        stimulusHtml_en: stimulusHtml,
        options: allVals.map((v, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: `${v} أزواج`,
          text_en: `${v} pairs`,
          value: v
        })),
        correctIndex,
        explanation_ar: `الزوج 1 والزوج 3 متطابقان تماماً، بينما يحتوي الزوجان 2 و 4 على رموز مختلفة (& مقابل % و ~ مقابل -)، فالعدد = 2.`,
        explanation_en: `Pairs 1 and 3 are exact matches; pairs 2 and 4 differ by one symbol, yielding 2 matches.`
      };
    }
  },

  // 7. Threshold Exceeding Bar Spot
  {
    id: 'speed_bar_chart_threshold',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2],
    expectedTimeSeconds: 20,
    generate(prng, difficulty = 1) {
      // 4 bars: heights [40, 65, 85, 55], threshold is 75. Bar C is 85!
      const correctAns_ar = 'العمود رقم 3 (C)';
      const correctAns_en = 'Bar #3 (C)';

      const distractors = [
        { ar: 'العمود رقم 1 (A)', en: 'Bar #1 (A)' },
        { ar: 'العمود رقم 2 (B)', en: 'Bar #2 (B)' },
        { ar: 'العمود رقم 4 (D)', en: 'Bar #4 (D)' }
      ];

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const makeBarSvg = (label) => `<svg viewBox="0 0 380 130" xmlns="http://www.w3.org/2000/svg">
        <rect width="380" height="130" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255,255,255,0.1)"/>
        <!-- Threshold dashed line -->
        <line x1="30" y1="40" x2="295" y2="40" stroke="#ef4444" stroke-width="2" stroke-dasharray="5 3"/>
        <text x="305" y="44" fill="#ef4444" font-size="11" font-weight="700">${label}</text>
        <!-- Bars -->
        <rect x="50" y="70" width="35" height="40" rx="4" fill="#38bdf8"/>
        <text x="67" y="122" fill="#f8fafc" font-size="11" text-anchor="middle">A</text>
        <rect x="120" y="50" width="35" height="60" rx="4" fill="#38bdf8"/>
        <text x="137" y="122" fill="#f8fafc" font-size="11" text-anchor="middle">B</text>
        <rect x="190" y="25" width="35" height="85" rx="4" fill="#38bdf8"/>
        <text x="207" y="122" fill="#f8fafc" font-size="11" text-anchor="middle">C</text>
        <rect x="260" y="60" width="35" height="50" rx="4" fill="#38bdf8"/>
        <text x="277" y="122" fill="#f8fafc" font-size="11" text-anchor="middle">D</text>
      </svg>`;

      const stimulusHtml_ar = makeBarSvg('حد 75');
      const stimulusHtml_en = makeBarSvg('Limit 75');

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `أي عمود بياني يتجاوز الخط الأحمر المرجعي (حد 75) الموضح؟`,
        questionText_en: `Which bar strictly surpasses the red reference threshold line?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `العمود C هو الوحيد الذي يرتفع فوق مستوى الخط الأحمر المتقطع.`,
        explanation_en: `Bar C is the sole bar that crosses above the horizontal threshold marker.`
      };
    }
  },

  // 8. Pure 2D Rotation vs Reflection Detection
  {
    id: 'speed_rotation_vs_reflection',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2],
    expectedTimeSeconds: 25,
    generate(prng, difficulty = 1) {
      // An 'R' letter or asymmetrical L shape
      // Only ONE option is a valid 2D planar rotation.
      // Other 3 options are mirrored / inverted reflections!
      const correctAns_ar = 'الشكل (A) يمثل دوراناً محضاً بزاوية 90 درجة مع عقارب الساعة';
      const correctAns_en = 'Figure A is a pure 90-degree clockwise planar rotation';

      const distractors = [
        { ar: 'الشكل (B) وهو انعكاس مرآوي مقلوب أفقياً', en: 'Figure B is a horizontal mirror reflection' },
        { ar: 'الشكل (C) وهو انعكاس مرآوي مقلوب رأسياً', en: 'Figure C is a vertical mirror reflection' },
        { ar: 'الشكل (D) وهو انعكاس قطري مائل معكوس', en: 'Figure D is a diagonal flipped reflection' }
      ];

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-muted">الشكل الأصلي المرجعي: حرف (L) مع نقطة في زاويته اليمنى</div>
          <div class="iq-stimulus-card-title">المطلوب: تمييز الدوران الحقيقي دون انعكاس مرآوي</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-muted">Reference Shape: letter (L) with a dot in its right corner</div>
          <div class="iq-stimulus-card-title">Target: Identify pure planar rotation without mirror reflection</div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `أي من الخيارات التالية يمثل دوراناً مستوياً حقيقياً للشكل في نفس المستوى ثنائي الأبعاد دون قلبه كمرآة؟`,
        questionText_en: `Which choice represents a pure planar rotation without any mirror reflection?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `الدوران المستوي يحافظ على الاتجاهية الكيرالية (Chirality) للشكل دون قلب اليمين يساراً كالانعكاس.`,
        explanation_en: `Planar rotation preserves chirality, whereas reflection inverts relative handedness.`
      };
    }
  },

  // 9. Overlapping Enclosed Regions Count
  {
    id: 'speed_overlapping_regions',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2],
    expectedTimeSeconds: 25,
    generate(prng, difficulty = 1) {
      // 3 overlapping circles (Venn of 3): exactly 7 enclosed sub-regions
      const correctVal = 7;
      const distSet = new Set([6, 8, 5]);
      const distractors = Array.from(distSet);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const stimulusSvg = `<svg viewBox="0 0 380 120" xmlns="http://www.w3.org/2000/svg">
        <rect width="380" height="120" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255,255,255,0.1)"/>
        <circle cx="165" cy="50" r="32" fill="none" stroke="#38bdf8" stroke-width="2"/>
        <circle cx="215" cy="50" r="32" fill="none" stroke="#ec4899" stroke-width="2"/>
        <circle cx="190" cy="85" r="32" fill="none" stroke="#f59e0b" stroke-width="2"/>
      </svg>`;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `كم عدد المناطق المغلقة المستقلة التي تتكون من تداخل الدوائر الثلاث الموضحة في الرسم؟`,
        questionText_en: `How many distinct bounded enclosed regions are created by the 3 overlapping circles?`,
        stimulusHtml: stimulusSvg,
        options: allVals.map((v, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: `${v} مناطق مغلقة`,
          text_en: `${v} regions`,
          value: v
        })),
        correctIndex,
        explanation_ar: `تداخل 3 دوائر يشكل 3 مناطق أحادية + 3 مناطق ثنائية مشتركة + 1 منطقة ثلاثية في المركز = 7 مناطق.`,
        explanation_en: `Three mutually overlapping circles form 3 single + 3 double + 1 triple intersection = 7 enclosed regions.`
      };
    }
  },

  // 10. Elapsed Time Between Two Clocks
  {
    id: 'speed_elapsed_clock_minutes',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2],
    expectedTimeSeconds: 25,
    generate(prng, difficulty = 1) {
      // Clock 1: 02:15, Clock 2: 03:05 -> Elapsed = 50 mins
      const correctVal = 50;
      const distSet = new Set([45, 55, 40]);
      const distractors = Array.from(distSet);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-highlight" style="font-family: monospace; font-size: 1.35rem; letter-spacing: 0.05em;">
            02:15 &nbsp;→&nbsp; 03:05
          </div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو عدد الدقائق المنقضية تماماً بين التوقيت الأول (02:15) والتوقيت الثاني (03:05)؟`,
        questionText_en: `How many minutes elapsed between 02:15 and 03:05?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en: stimulusHtml_ar,
        options: allVals.map((v, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: `${v} دقيقة`,
          text_en: `${v} minutes`,
          value: v
        })),
        correctIndex,
        explanation_ar: `من 02:15 إلى 03:00 = 45 دقيقة، ومن 03:00 إلى 03:05 = 5 دقائق. المجموع = 50 دقيقة.`,
        explanation_en: `45 minutes to 03:00 plus 5 minutes to 03:05 equals 50 minutes.`
      };
    }
  },

  // 11. Quadrant Dot Count Discrimination
  {
    id: 'speed_quadrant_dot_count',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2],
    expectedTimeSeconds: 20,
    generate(prng, difficulty = 1) {
      // Count dots in upper right quadrant: e.g. 5 dots
      const correctVal = 5;
      const distSet = new Set([4, 6, 7]);
      const distractors = Array.from(distSet);

      const allVals = prng.shuffle([correctVal, ...distractors]);
      const correctIndex = allVals.indexOf(correctVal);

      const stimulusSvg = `<svg viewBox="0 0 380 120" xmlns="http://www.w3.org/2000/svg">
        <rect width="380" height="120" rx="12" fill="rgba(14, 22, 40, 0.65)" stroke="rgba(255,255,255,0.1)"/>
        <!-- Quadrant divider -->
        <line x1="190" y1="15" x2="190" y2="105" stroke="rgba(255,255,255,0.2)" stroke-width="1.5"/>
        <line x1="130" y1="60" x2="250" y2="60" stroke="rgba(255,255,255,0.2)" stroke-width="1.5"/>
        <!-- 5 dots in top-right (x > 190, y < 60) -->
        <circle cx="205" cy="30" r="4.5" fill="#38bdf8"/>
        <circle cx="225" cy="25" r="4.5" fill="#38bdf8"/>
        <circle cx="240" cy="40" r="4.5" fill="#38bdf8"/>
        <circle cx="215" cy="48" r="4.5" fill="#38bdf8"/>
        <circle cx="235" cy="52" r="4.5" fill="#38bdf8"/>
        <!-- Other quadrants have few dots -->
        <circle cx="160" cy="35" r="4.5" fill="#94a3b8"/>
        <circle cx="170" cy="85" r="4.5" fill="#94a3b8"/>
        <circle cx="220" cy="85" r="4.5" fill="#94a3b8"/>
      </svg>`;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `كم عدد النقاط الزرقاء المتواجدة في الربع العلوي الأيمن فقط؟`,
        questionText_en: `How many blue dots are located specifically in the upper-right quadrant?`,
        stimulusHtml: stimulusSvg,
        options: allVals.map((v, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: `${v} نقاط`,
          text_en: `${v} dots`,
          value: v
        })),
        correctIndex,
        explanation_ar: `بالعد الدقيق في الربع العلوي الأيمن، توجد 5 نقاط زرقاء.`,
        explanation_en: `Counting the upper-right quadrant yields exactly 5 blue dots.`
      };
    }
  },

  // 12. Angle Magnitude Classification
  {
    id: 'speed_obtuse_angle_spot',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2],
    expectedTimeSeconds: 20,
    generate(prng, difficulty = 1) {
      const correctAns_ar = 'الزاوية (C) ذات الانفراج 125 درجة';
      const correctAns_en = 'Angle C (125 degrees)';

      const distractors = [
        { ar: 'الزاوية (A) ذات الانفراج 45 درجة', en: 'Angle A (45 degrees)' },
        { ar: 'الزاوية (B) ذات الانفراج 90 درجة', en: 'Angle B (90 degrees)' },
        { ar: 'الزاوية (D) ذات الانفراج 60 درجة', en: 'Angle D (60 degrees)' }
      ];

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">المطلوب: تمييز الزاوية المنفرجة</div>
          <div class="iq-stimulus-card-body">(الزاوية التي يزيد قياسها عن 90° وأقل من 180°)</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">Target: Identify the Obtuse Angle</div>
          <div class="iq-stimulus-card-body">(Angle strictly greater than 90° and less than 180°)</div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `أي من الزوايا التالية تصنف هندسياً بأنها زاوية منفرجة (تتجاوز 90 درجة)؟`,
        questionText_en: `Which of the following is geometrically classified as an obtuse angle (> 90 degrees)?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `الزاوية المنفرجة هي التي قياسها أكبر من 90° وأصغر من 180°، وتنطبق على الزاوية 125°.`,
        explanation_en: `An obtuse angle is strictly greater than 90°, matching 125°.`
      };
    }
  },

  // 13. Perpendicular Lines Identification
  {
    id: 'speed_perpendicular_lines',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2],
    expectedTimeSeconds: 20,
    generate(prng, difficulty = 1) {
      const correctAns_ar = 'المستقيمان اللذان يلتقيان بزاوية قائمة تماماً (90°)';
      const correctAns_en = 'Two lines intersecting at exactly 90 degrees';

      const distractors = [
        { ar: 'المستقيمان اللذان لا يلتقيان أبداً ويسيران بمحاذاة تامة', en: 'Two parallel lines never intersecting' },
        { ar: 'المستقيمان اللذان يلتقيان بزاوية حادة مقدارها 45 درجة', en: 'Two lines meeting at 45 degrees' },
        { ar: 'المستقيمان اللذان ينحرفان بزاوية منفرجة 135 درجة', en: 'Two lines meeting at 135 degrees' }
      ];

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">المفهوم الهندسي: التعامد الكامل (Perpendicularity)</div>
          <div class="iq-stimulus-card-body">أي زوج من المستقيمات يلتقيان بزاوية قائمة تماماً (90°)؟</div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-title">Geometric Concept: Perpendicularity</div>
          <div class="iq-stimulus-card-body">Which pair of lines intersects at an exact right angle (90°)?</div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو الشرط الهندسي الدقيق الذي يحدد تعامد مستقيمين في المستوى الإقليدي؟`,
        questionText_en: `What geometric condition defines perpendicular lines in Euclidean geometry?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `التعامد يعني تقاطع مستقيمين بحيث يشكلان زاوية قياسها 90 درجة بالتمام.`,
        explanation_en: `Perpendicularity requires the intersection angle to be precisely 90 degrees.`
      };
    }
  },

  // 14. Missing Internal Spoke Wheel
  {
    id: 'speed_wheel_spoke_spot',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2],
    expectedTimeSeconds: 20,
    generate(prng, difficulty = 1) {
      // 8-spoke wheel missing 1 spoke at 90 deg (top)
      const correctAns_ar = 'الشعاع الرأسي الموجه للأعلى مباشرة (موضع الساعة 12)';
      const correctAns_en = 'The top vertical spoke (12 o clock position)';

      const distractors = [
        { ar: 'الشعاع الأفقي الموجه نحو أقصى اليمين', en: 'Rightmost horizontal spoke' },
        { ar: 'الشعاع الرأسي الموجه للأسفل مباشرة', en: 'Bottom vertical spoke' },
        { ar: 'الشعاع المائل بزاوية 45 درجة للأسفل', en: '45-degree lower diagonal spoke' }
      ];

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusSvg = `<svg viewBox="0 0 160 140" xmlns="http://www.w3.org/2000/svg" style="max-height:130px; margin:0 auto; display:block;">
        <circle cx="80" cy="70" r="50" fill="none" stroke="#38bdf8" stroke-width="2.5"/>
        <circle cx="80" cy="70" r="8" fill="#38bdf8"/>
        <!-- Spokes at right, bottom, left, and diagonals. Top (12 o'clock) is missing! -->
        <line x1="80" y1="70" x2="130" y2="70" stroke="#f8fafc" stroke-width="2"/>
        <line x1="80" y1="70" x2="80" y2="120" stroke="#f8fafc" stroke-width="2"/>
        <line x1="80" y1="70" x2="30" y2="70" stroke="#f8fafc" stroke-width="2"/>
        <line x1="80" y1="70" x2="115" y2="105" stroke="#f8fafc" stroke-width="2"/>
        <line x1="80" y1="70" x2="45" y2="105" stroke="#f8fafc" stroke-width="2"/>
        <line x1="80" y1="70" x2="115" y2="35" stroke="#f8fafc" stroke-width="2"/>
        <line x1="80" y1="70" x2="45" y2="35" stroke="#f8fafc" stroke-width="2"/>
      </svg>`;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `بالملاحظة السريعة لعجلة الأشعة الموضحة، أي شعاع مفقود لكي يكتمل التماثل الدوراني التام؟`,
        questionText_en: `Quickly inspecting the spoked wheel, which spoke is missing for full rotational symmetry?`,
        stimulusHtml: stimulusSvg,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `الشعاع الرأسي العلوي عند موضع الساعة 12 هو الشعاع الوحيد الغائب عن الدائرة.`,
        explanation_en: `The 12 o'clock vertical spoke is the missing element.`
      };
    }
  },

  // 15. Number Inversion Spotting
  {
    id: 'speed_number_inversion_spot',
    domain: 'processing_speed',
    supportedDifficulties: [1, 2],
    expectedTimeSeconds: 20,
    generate(prng, difficulty = 1) {
      // Stream: 66, 66, 99, 66.
      const correctAns_ar = 'العدد [ 99 ] في الموضع الثالث';
      const correctAns_en = 'Number [ 99 ] at third position';

      const distractors = [
        { ar: 'العدد [ 66 ] في الموضع الأول', en: 'Number [ 66 ] at first position' },
        { ar: 'العدد [ 66 ] في الموضع الثاني', en: 'Number [ 66 ] at second position' },
        { ar: 'العدد [ 66 ] في الموضع الرابع', en: 'Number [ 66 ] at fourth position' }
      ];

      const allChoices = prng.shuffle([
        { ar: correctAns_ar, en: correctAns_en, isCorrect: true },
        ...distractors
      ]);
      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml_ar = `
        <div class="iq-stimulus-card">
          <div class="iq-stimulus-card-highlight" style="font-family: monospace; font-size: 1.35rem; letter-spacing: 0.1em;">
            [ 66 ] &nbsp;•&nbsp; [ 66 ] &nbsp;•&nbsp; [ 99 ] &nbsp;•&nbsp; [ 66 ]
          </div>
        </div>
      `;

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `أي عنصر في السلسلة العددية مقلوب ومختلف عن النمط الموحد السائد؟`,
        questionText_en: `Which token in the sequence is inverted and differs from the dominant pattern?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en: stimulusHtml_ar,
        options: allChoices.map((c, idx) => ({
          id: String.fromCharCode(65 + idx),
          text_ar: c.ar,
          text_en: c.en,
          value: c.ar
        })),
        correctIndex,
        explanation_ar: `العدد 99 مقلوب بصرياً مقارنة بسائر عناصر السلسلة الموحدة (66).`,
        explanation_en: `Number 99 represents the inverted outlier among the 66 tokens.`
      };
    }
  }
];
